import {
    BarChart3,
    BookOpen,
    ChevronDown,
    Clock,
    Coins,
    CreditCard,
    DollarSign,
    FileText,
    Gift,
    HelpCircle,
    LayoutDashboard,
    LogOut,
    MessageSquare,
    Plus,
    Package,
    Settings,
    Tag,
    UserCheck,
    UserCog,
    Users,
} from 'lucide-react';
import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import logo from '../../assets/img/logo.png';
import styles from '../../assets/scss/layout/AdminSidebar.module.scss';
import { PERMISSIONS } from '../../config/permissions';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuthStore } from '../../stores/authStore';
import { version } from '../../config/version-code';

const P = PERMISSIONS;

export default function AdminSidebar({ collapsed, mobileOpen, onClose, shopName }) {
    const navigate = useNavigate();
    const { logout, user } = useAuthStore();
    const { hasPermission, hasSome } = usePermissions();
    const [reportOpen, setReportOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/admin/login');
    };

    const linkClass = ({ isActive }) =>
        `${styles.adminSidebarLink}${isActive ? ` ${styles.adminSidebarLinkActive}` : ''}`;

    const handleNavClick = () => {
        if (onClose) onClose();
    };

    const menuItems = [
        {
            to: '/admin/dashboard',
            icon: <LayoutDashboard />,
            label: 'Tổng quan',
            show: true,
            end: true,
        },
        {
            to: '/checkin',
            icon: <UserCheck />,
            label: 'Check-in',
            show: true,
        },
        {
            type: 'create-order',
            show: hasSome([P.BILL.VIEW_LIST, P.BILL.CREATE]),
        },
        {
            to: '/admin/bills',
            icon: <FileText />,
            label: 'Hoá đơn',
            show: hasSome([P.BILL.VIEW_LIST, P.BILL.CREATE]),
        },
        {
            to: '/admin/customers',
            icon: <Users />,
            label: 'Khách hàng',
            show: hasPermission(P.CUSTOMER.VIEW),
        },
        {
            to: '/admin/services',
            icon: <Package />,
            label: 'Sản Phẩm/Dịch vụ',
            show: hasPermission(P.SERVICE.VIEW_LIST),
        },
        {
            to: '/admin/employees',
            icon: <UserCog />,
            label: 'Nhân viên',
            show: hasPermission(P.EMPLOYEE.VIEW),
        },
        {
            type: 'submenu',
            icon: <BarChart3 />,
            label: 'Báo cáo',
            show: hasSome([
                P.REPORT.DASHBOARD_VIEW,
                P.REPORT.REVENUE_VIEW,
                P.REPORT.SALES_VIEW,
                P.REPORT.SALES_DETAIL_VIEW,
                P.REPORT.REVENUE_BILL_DETAIL_VIEW,
                P.REPORT.CUSTOMER_VIEW,
                P.REPORT.END_DAY_VIEW,
            ]),
            children: [
                { to: '/admin/reports', label: 'Tổng quan', show: hasPermission(P.REPORT.DASHBOARD_VIEW) },
                { to: '/admin/reports/customers', label: 'BC Khách hàng', show: hasPermission(P.REPORT.CUSTOMER_VIEW) },
                { to: '/admin/reports/revenue', label: 'BC Doanh thu', show: hasPermission(P.REPORT.REVENUE_VIEW) },
                { to: '/admin/reports/end-day', label: 'BC Cuối ngày', show: hasPermission(P.REPORT.END_DAY_VIEW) },
                { to: '/admin/reports/products', label: 'BC Bán hàng', show: hasPermission(P.REPORT.SALES_VIEW) },
                {
                    to: '/admin/reports/products-detail',
                    label: 'Chi tiết bán hàng',
                    show: hasPermission(P.REPORT.SALES_DETAIL_VIEW),
                },
                {
                    to: '/admin/reports/bills-detail',
                    label: 'Chi tiết hoá đơn',
                    show: hasPermission(P.REPORT.REVENUE_BILL_DETAIL_VIEW),
                },
            ],
        },
        {
            to: '/admin/revenue-book',
            icon: <BookOpen />,
            label: 'Sổ doanh thu',
            show: hasPermission(P.REVENUE_BOOK.VIEW),
        },
        {
            to: '/admin/commissions',
            icon: <Coins />,
            label: 'Hoa hồng',
            show: hasPermission(P.COMMISSION.VIEW),
        },
        {
            to: '/admin/points',
            icon: <Gift />,
            label: 'Quy tắc điểm',
            show: true,
        },
        {
            to: '/admin/attributes',
            icon: <Tag />,
            label: 'Thuộc tính',
            show: hasPermission(P.ATTRIBUTE.VIEW),
        },
        {
            to: '/admin/feedback',
            icon: <MessageSquare />,
            label: 'Đánh giá',
            show: hasPermission(P.FEEDBACK.VIEW),
        },
        {
            to: '/admin/shifts',
            icon: <Clock />,
            label: 'Ca làm việc',
            show: hasPermission(P.SHIFT_WORK.VIEW),
        },
        {
            to: '/admin/salary',
            icon: <DollarSign />,
            label: 'Quản lý nhân sự',
            show: hasPermission(P.SALARY.VIEW),
        },
        {
            to: '/admin/banking',
            icon: <CreditCard />,
            label: 'Ngân hàng',
            show: true,
        },
        {
            to: '/admin/settings',
            icon: <Settings />,
            label: 'Cài đặt',
            show: true,
        },
        {
            type: 'external',
            href: '/navo/huong-dan/index.html',
            icon: <HelpCircle />,
            label: 'Hướng dẫn sử dụng',
            show: true,
        },
    ];

    return (
        <aside
            className={`${styles.adminSidebar}${collapsed ? ` ${styles.adminSidebarCollapsed}` : ''}${mobileOpen ? ` ${styles.adminSidebarMobileOpen}` : ''}`}
        >
            {/* Logo */}
            <div className={styles.adminSidebarHeader}>
                <div className={styles.adminSidebarLogoIcon}>
                    <img src={logo} alt="Navo" style={{ width: 24, height: 24, objectFit: 'contain' }} />
                </div>
                <div className={styles.adminSidebarLogoText}>
                    <span className={styles.adminSidebarLogoTitle}>Navo Sổ Tiệm</span>
                    <span className={styles.adminSidebarLogoSub}>
                        {shopName || user?.name || user?.userName || 'Cửa hàng'}
                    </span>
                </div>
            </div>

            {/* Navigation */}
            <nav className={styles.adminSidebarNav}>
                {menuItems.map((item, idx) => {
                    if (!item.show) return null;

                    if (item.type === 'create-order') {
                        return (
                            <button
                                key="create-order"
                                className={styles.adminSidebarLink}
                                onClick={() => {
                                    navigate('/admin/bills/create');
                                    handleNavClick();
                                }}
                            >
                                <Plus />
                                <span>Tạo đơn</span>
                            </button>
                        );
                    }

                    // External link (static guide hosted at /navo/huong-dan/)
                    if (item.type === 'external') {
                        return (
                            <a
                                key={item.href}
                                href={item.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.adminSidebarLink}
                                onClick={handleNavClick}
                            >
                                {item.icon}
                                <span>{item.label}</span>
                            </a>
                        );
                    }

                    // Submenu (Báo cáo)
                    if (item.type === 'submenu') {
                        return (
                            <div key={idx}>
                                <button
                                    className={`${styles.adminSidebarLink}${reportOpen ? ` ${styles.adminSidebarLinkActive}` : ''}`}
                                    onClick={() => setReportOpen(!reportOpen)}
                                >
                                    {item.icon}
                                    <span>{item.label}</span>
                                    <ChevronDown
                                        size={16}
                                        className={`${styles.adminSidebarChevron}${reportOpen ? ` ${styles.adminSidebarChevronOpen}` : ''}`}
                                    />
                                </button>
                                <div
                                    className={`${styles.adminSidebarSubmenu}${reportOpen ? ` ${styles.adminSidebarSubmenuOpen}` : ''}`}
                                >
                                    {item.children
                                        .filter((c) => c.show)
                                        .map((child) => (
                                            <NavLink
                                                key={child.to}
                                                to={child.to}
                                                className={({ isActive }) =>
                                                    `${styles.adminSidebarSubmenuLink}${isActive ? ` ${styles.adminSidebarSubmenuLinkActive}` : ''}`
                                                }
                                                onClick={handleNavClick}
                                                end
                                            >
                                                {child.label}
                                            </NavLink>
                                        ))}
                                </div>
                            </div>
                        );
                    }

                    return (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            className={linkClass}
                            onClick={handleNavClick}
                            end={item.end}
                        >
                            {item.icon}
                            <span>{item.label}</span>
                        </NavLink>
                    );
                })}
            </nav>

            {/* Footer */}
            <div className={styles.adminSidebarFooter}>
                <button className={styles.adminSidebarLogout} onClick={handleLogout}>
                    <LogOut />
                    <span>Đăng xuất</span>
                </button>
                <p className={styles.adminSidebarVersion}>Phiên bản: {version}</p>
            </div>
        </aside>
    );
}
